import axiosInstance from "../../services/axios";
import { ActionTypes } from "../type";
import { setSession, resetSession } from "../../services/sessions";
import { validateToken, validateTokenValue } from "../../services/jwt";
import jwtDecode from "jwt-decode";

const getAuthErrorMessage = (err, fallbackMessage) =>
  err.response?.data?.detail || fallbackMessage;

const FRANCHISE_LOGIN_BLOCKED_MESSAGE =
  "Franchise users must log in through the Channel Partner portal.";

const isEnabledFlag = (value) => {
  if (value === true || value === 1) {
    return true;
  }

  const normalizedValue = String(value ?? "").trim().toLowerCase();
  return normalizedValue === "true" || normalizedValue === "1";
};

const getTokenSubject = (accessToken) => {
  const decoded = jwtDecode(accessToken);

  if (typeof decoded.sub === "object" && decoded.sub !== null) {
    return decoded.sub;
  }

  return JSON.parse(
    decoded.sub.replace(/'/g, '"').replace(/None/g, "null")
  );
};

const clearAuthenticationStorage = () => {
  [
    "franchise_payment_mode",
    "is_franchise_employee",
    "is_super_franchise",
    "employee_code_id",
    "franchise_id",
    "sub_franchise",
    "new_registration_franchise_id",
    "zone_id",
  ].forEach((key) => localStorage.removeItem(key));
  resetSession();
};

const createFranchiseLoginError = () => {
  const error = new Error(FRANCHISE_LOGIN_BLOCKED_MESSAGE);
  error.code = "FRANCHISE_LOGIN_BLOCKED";
  return error;
};

const getTokenSubjectObject = () => {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    return {};
  }

  try {
    const subObject = getTokenSubject(accessToken);

    const franchiseId = subObject?.franchise_id;
    if (franchiseId !== undefined) {
      localStorage.setItem("franchise_id", franchiseId);
    }

    const employeeCodeId = subObject?.employee_code_id;
    if (employeeCodeId !== undefined) {
      localStorage.setItem("employee_code_id", employeeCodeId);
    }

    if ("franchise_payment_mode" in subObject) {
      localStorage.setItem(
        "franchise_payment_mode",
        subObject.franchise_payment_mode
      );
    }

    if ("is_franchise_employee" in subObject) {
      localStorage.setItem(
        "is_franchise_employee",
        subObject.is_franchise_employee
      );
    }

    if ("is_super_franchise" in subObject) {
      localStorage.setItem("is_super_franchise", subObject.is_super_franchise);
    }

    if ("sub_franchise" in subObject) {
      localStorage.setItem("sub_franchise", subObject.sub_franchise);
    }

    return subObject;
  } catch (error) {
    console.error("Failed to decode JWT", error);
    return {};
  }
};

const isFranchiseEmployeeToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }

  try {
    const subObject = getTokenSubject(accessToken);
    return isEnabledFlag(subObject?.is_franchise_employee);
  } catch (error) {
    console.error("Failed to decode JWT", error);
    return false;
  }
};

const finalizeAuthenticatedUser = async (dispatch) => {
  const response = await axiosInstance.get("/hrms/me");
  const { data: user } = response;

  if (isEnabledFlag(user?.is_franchise_employee)) {
    clearAuthenticationStorage();
    throw createFranchiseLoginError();
  }

  if (user.zone_id) {
    localStorage.setItem("zone_id", user.zone_id);
  }

  const subObject = getTokenSubjectObject();

  dispatch({
    type: ActionTypes.LOGIN,
    payload: {
      user,
      subObject,
    },
  });

  return user;
};

export const getToken = async (username, password) => {
  const formData = new FormData();
  formData.append("username", username);
  formData.append("password", password);
  try {
    const response = await axiosInstance.post("/auth/login", formData);
    setSession(response.data.access_token, response.data.refresh_token);
  } catch (error) {
    throw error;
  }
};

export const initialize = (isAuthenticated, user, token_values) => {
  return {
    type: ActionTypes.INITIALIZE,
    payload: {
      isAuthenticated,
      user,
      token_values,
    },
  };
};

export const loginUser = (username, password, onError) => async (dispatch) => {
  try {
    await getToken(username, password);
    const accessToken = localStorage.getItem("accessToken");
    if (isFranchiseEmployeeToken(accessToken)) {
      clearAuthenticationStorage();
      if (onError) onError(FRANCHISE_LOGIN_BLOCKED_MESSAGE);
      return false;
    }
    await finalizeAuthenticatedUser(dispatch);
    return true;
  } catch (err) {
    const errorMessage =
      err?.code === "FRANCHISE_LOGIN_BLOCKED"
        ? err.message
        : getAuthErrorMessage(
            err,
            "Invalid email or password. Please try again."
          );
    if (onError) onError(errorMessage);
    return false;
  }
};

export const requestFranchiseEmailOtp = async (email) => {
  return axiosInstance.post("/auth/franchise/email-otp/request", { email });
};

export const loginUserWithOtp = (email, otp, onError) => async (dispatch) => {
  try {
    const response = await axiosInstance.post("/auth/franchise/email-otp/verify", {
      email,
      otp,
    });
    setSession(response.data.access_token, response.data.refresh_token);
    if (isFranchiseEmployeeToken(response.data.access_token)) {
      clearAuthenticationStorage();
      if (onError) onError(FRANCHISE_LOGIN_BLOCKED_MESSAGE);
      return false;
    }
    await finalizeAuthenticatedUser(dispatch);
    return true;
  } catch (err) {
    const errorMessage =
      err?.code === "FRANCHISE_LOGIN_BLOCKED"
        ? err.message
        : getAuthErrorMessage(err, "Invalid OTP. Please try again.");
    if (onError) onError(errorMessage);
    return false;
  }
};

export const AuthProvider = () => async (dispatch) => {
  dispatch({
    type: ActionTypes.LOADING,
    payload: {
      loading: true,
    },
  });
  try {
    const accessToken = localStorage.getItem("accessToken");
    const token_values = validateTokenValue(accessToken);
    if (accessToken && validateToken(accessToken)) {
      if (isFranchiseEmployeeToken(accessToken)) {
        clearAuthenticationStorage();
        dispatch(initialize(false, null, null));
      } else {
        setSession(accessToken);
        const response = await axiosInstance.get("/hrms/me");
        const { data: user } = response;

        if (isEnabledFlag(user?.is_franchise_employee)) {
          clearAuthenticationStorage();
          dispatch(initialize(false, null, null));
        } else {
          dispatch(initialize(true, user, token_values));
        }
      }
    } else {
      dispatch({
        type: ActionTypes.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  } catch (error) {
    dispatch({
      type: ActionTypes.INITIALIZE,
      payload: {
        isAuthenticated: false,
        user: null,
      },
    });
  }

  dispatch({
    type: ActionTypes.LOADING,
    payload: {
      loading: false,
    },
  });
};

export const logout = () => async (dispatch) => {
  localStorage.removeItem("franchise_payment_mode");
  localStorage.removeItem("is_franchise_employee");
  localStorage.removeItem("is_super_franchise");
  localStorage.removeItem("employee_code_id");
  localStorage.removeItem("franchise_id");
  localStorage.removeItem("fcm_token");
  localStorage.removeItem("sub_franchise");
  localStorage.removeItem("new_registration_franchise_id");
  localStorage.removeItem("zone_id");
  resetSession();
  dispatch({
    type: ActionTypes.LOGOUT,
  });
};
